<template>
  <header class="report-header card">
    <div class="report-logo">
      <img class="logo-light" :src="logos.light" alt="AVA" />
      <img class="logo-dark" :src="logos.dark" alt="AVA" />
    </div>

    <div class="report-title-block">
      <div class="report-title-row">
        <h1 class="report-title">{{ title }}</h1>
        <span v-if="!approved" class="chip chip--proposed">{{ proposedLabel }}</span>
      </div>
      <div class="report-subline">
        <span>{{ mode }}</span>
        <span class="sep"></span>
        <strong>{{ scope }}</strong>
      </div>
    </div>

    <div class="meta-strip">
      <div class="meta-item">
        <span class="meta-label">Job / Tag</span>
        <span class="meta-value">{{ jobLabel }}<small>{{ jobSub }}</small></span>
      </div>
      <div class="meta-item">
        <span class="meta-label">AI Model</span>
        <span class="meta-value">{{ aiModel || notRecorded }}<small>{{ modelSub }}</small></span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Date / Time</span>
        <span class="meta-value mono">{{ dateLabel }}<small>Job created</small></span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Inspector Review</span>
        <span class="meta-value review-value" :class="reviewClass">{{ reviewStatus }}</span>
        <small class="meta-label">{{ decidedCount }} of {{ totalCount }} findings decided</small>
      </div>
    </div>
  </header>
</template>

<script>
  import { $formatDate } from '@/utils';
  import { AVA_LOGOS } from '@Share/config/logos';
  import { REVIEW_STATUS } from '../../utils/findingsMetrics';
  import { EM_DASH } from '../../utils/reportFormat';
  import { REPORT_TITLE, REPORT_MODE, REPORT_SCOPE, PROPOSED_LABEL, DATE_FORMAT, NOT_RECORDED } from './reportConstants';

  const REVIEW_CLASS = Object.freeze({
    [REVIEW_STATUS.PENDING]: 'is-pending',
    [REVIEW_STATUS.IN_REVIEW]: 'is-inreview',
    [REVIEW_STATUS.APPROVED]: 'is-approved',
  });

  export default {
    name: 'ReportHeader',
    props: {
      jobLabel: { type: String, default: EM_DASH },
      jobSub: { type: String, default: '' },
      aiModel: { type: String, default: '' },
      modelSub: { type: String, default: '' },
      createDate: { type: String, default: null },
      reviewStatus: { type: String, default: REVIEW_STATUS.PENDING },
      decidedCount: { type: Number, default: 0 },
      totalCount: { type: Number, default: 0 },
    },
    data() {
      return {
        title: REPORT_TITLE,
        mode: REPORT_MODE,
        scope: REPORT_SCOPE,
        proposedLabel: PROPOSED_LABEL,
        emDash: EM_DASH,
        notRecorded: NOT_RECORDED,
        logos: { light: AVA_LOGOS.castletonGreen, dark: AVA_LOGOS.antiFlashWhite },
      };
    },
    computed: {
      approved() {
        return this.reviewStatus === REVIEW_STATUS.APPROVED;
      },
      reviewClass() {
        return REVIEW_CLASS[this.reviewStatus] || REVIEW_CLASS[REVIEW_STATUS.PENDING];
      },
      dateLabel() {
        return this.createDate ? $formatDate(this.createDate, DATE_FORMAT) : EM_DASH;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .report-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: stretch;
  }

  .report-logo {
    display: flex;
    align-items: center;
    padding: 0 var(--ds-space-lg);
    border-right: 1px solid var(--aa-border-default);

    img { height: 34px; width: auto; display: block; }
    .logo-dark { display: none; }
  }
  .theme--dark .report-logo .logo-light { display: none; }
  .theme--dark .report-logo .logo-dark { display: block; }

  .report-title-block { padding: 10px var(--ds-space-lg); display: flex; flex-direction: column; justify-content: center; gap: 4px; min-width: 0; }
  .report-title-row { display: flex; align-items: center; gap: var(--ds-space-sm); flex-wrap: wrap; }
  .report-title {
    margin: 0;
    font-family: var(--ds-font-family-heading);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.2;
    text-transform: uppercase;
    color: var(--aa-text-primary);
  }
  .report-subline { display: flex; align-items: center; gap: var(--ds-space-sm); flex-wrap: wrap; font-size: var(--ds-font-body); color: var(--aa-text-secondary); }
  .report-subline .sep { width: 1px; height: 16px; background: var(--aa-border-strong); }
  .report-subline strong { color: var(--aa-text-primary); font-weight: 500; letter-spacing: 0.02em; }

  .chip--proposed {
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 10px;
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-caption);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--aa-brand);
    border: 1px solid var(--aa-brand);
    white-space: nowrap;
  }

  .meta-strip { display: flex; }
  .meta-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding: 10px var(--ds-space-lg);
    border-left: 1px solid var(--aa-border-default);
    min-width: 150px;
  }
  .meta-label { font-size: var(--ds-font-caption); color: var(--aa-text-secondary); }
  .meta-value { font-size: var(--ds-font-body); font-weight: 600; white-space: nowrap; color: var(--aa-text-primary); }
  .meta-value small { display: block; font-weight: 400; color: var(--aa-text-muted); font-size: var(--ds-font-caption); }
  .meta-value.mono { font-variant-numeric: tabular-nums; }
  .review-value.is-pending { color: var(--aa-review-pending); }
  .review-value.is-inreview { color: var(--aa-review-inreview); }
  .review-value.is-approved { color: var(--aa-review-approved); }

  @media (max-width: 1100px) {
    .report-header { grid-template-columns: auto 1fr; }
    .meta-strip { grid-column: 1 / -1; border-top: 1px solid var(--aa-border-default); flex-wrap: wrap; }
    .meta-item { border-left: none; border-right: 1px solid var(--aa-border-default); flex: 1 1 160px; }
  }
  @media (max-width: 760px) {
    .report-logo { display: none; }
  }
</style>
